import { Box, BoxProps, SxProps, Theme, Typography } from "@mui/material";
import React, { ReactNode } from "react";
import { CHART_COLORS, CHART_DIMENSIONS, CHART_TYPOGRAPHY } from "./tokens";

type BadgeVariant =
  "neutral" | "negative" | "warning" | "info" | "confirmed" | "custom";

interface VariantStyle {
  bg: string;
  color: string;
}

const VARIANT_STYLES: Record<Exclude<BadgeVariant, "custom">, VariantStyle> = {
  neutral: {
    bg: CHART_COLORS.badgeNeutralBg,
    color: CHART_COLORS.badgeNeutralText,
  },
  negative: {
    bg: CHART_COLORS.badgeNegativeBg,
    color: CHART_COLORS.badgeNegativeText,
  },
  warning: {
    bg: CHART_COLORS.badgeWarningBg,
    color: CHART_COLORS.badgeWarningText,
  },
  info: { bg: CHART_COLORS.badgeInfoBg, color: CHART_COLORS.badgeInfoText },
  confirmed: {
    bg: CHART_COLORS.badgeConfirmedBg,
    color: CHART_COLORS.badgeConfirmedText,
  },
};

interface PatientInfoBadgeProps {
  variant?: BadgeVariant;
  bgColor?: string;
  textColor?: string;
  label: ReactNode;
  icon?: ReactNode;
  sx?: BoxProps["sx"];
  onClick?: () => void;
}

const PatientInfoBadge: React.FC<PatientInfoBadgeProps> = ({
  variant = "neutral",
  bgColor,
  textColor,
  label,
  icon,
  sx,
  onClick,
}) => {
  const resolvedBg =
    variant === "custom"
      ? (bgColor ?? CHART_COLORS.badgeNeutralBg)
      : VARIANT_STYLES[variant].bg;

  const resolvedColor =
    variant === "custom"
      ? (textColor ?? CHART_COLORS.badgeNeutralText)
      : VARIANT_STYLES[variant].color;

  const interactiveSx: SxProps<Theme> = onClick
    ? {
        cursor: "pointer",
        "&:hover": { filter: "brightness(0.93)" },
        "&:active": { filter: "brightness(0.87)" },
      }
    : {};

  return (
    <Box
      onClick={onClick}
      display="inline-flex"
      flexDirection="row"
      alignItems="center"
      gap="4px"
      sx={{
        bgcolor: resolvedBg,
        borderRadius: CHART_DIMENSIONS.infoBadge,
        p: "0 4px",
        // py: "2px",
        userSelect: "none",
        ...interactiveSx,
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: resolvedColor,
            "& svg": { fontSize: "12px" },
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        component="span"
        sx={{
          ...CHART_TYPOGRAPHY.body5,
          fontWeight: CHART_TYPOGRAPHY.weight.regular,
          color: resolvedColor,
          fontFamily: CHART_TYPOGRAPHY.fontFamily,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default PatientInfoBadge;
